import { Fragment } from "react";

import { ChevronDown, ChevronUp, History } from "lucide-react";

import {
  historyOrders,
  historySummary,
  isUsedUp,
  orderItemGroups,
  paidLineText,
  usageLogsOf,
  type HistoryFilter,
  type PackageOrder,
  type PackageOrderItem,
  type PackageUsageLog,
} from "./packageModelV4";

// Types
type PackageHistoryTabV4Props = {
  // data
  orders: PackageOrder[];
  usageLogs: PackageUsageLog[];
  loading: boolean;
  filter: HistoryFilter;
  openCards: number[];
  openLog: number | null;
  // callback
  onChangeFilter: (filter: HistoryFilter) => void;
  onToggleCard: (id: number) => void;
  onToggleLog: (id: number | null) => void;
};

// Const
const HISTORY_CHIPS: { key: HistoryFilter; label: string }[] = [
  { key: "all", label: "ทั้งหมด" },
  { key: "left", label: "มีคงเหลือ" },
  { key: "done", label: "ใช้ครบแล้ว" },
];

const PackageHistoryTabV4 = (props: PackageHistoryTabV4Props) => {
  const rows = historyOrders(props.orders, props.filter);

  // Render
  const renderChips = () => (
    <div className="hv4-pkg-chips" role="group" aria-label="กรองตามสถานะ">
      {HISTORY_CHIPS.map((chip) => (
        <button
          key={chip.key}
          type="button"
          className={`hv4-pkg-chip${props.filter === chip.key ? " hv4-pkg-chip--on" : ""}`}
          aria-pressed={props.filter === chip.key}
          onClick={() => props.onChangeFilter(chip.key)}
        >
          {chip.label} ({historyOrders(props.orders, chip.key).length})
        </button>
      ))}
    </div>
  );

  const renderItem = (item: PackageOrderItem) => {
    const total = Number(item.quantity) || 0;
    const left = Number(item.quantity_left) || 0;
    const logOpen = props.openLog === item.id;
    const logs = usageLogsOf(props.usageLogs, item.id);
    const label = `${logOpen ? "ซ่อน" : "ดู"}ประวัติการใช้งาน ${item.product_name}`;

    return (
      <Fragment key={item.id}>
        <div className="hv4-pkg-irow" role="row">
          <span role="cell" className="hv4-pkg-iname">
            {item.product_name}
          </span>
          <span role="cell">{total} ครั้ง</span>
          <span role="cell" className="hv4-pkg-c">
            {total - left}
          </span>
          <b role="cell" className={`hv4-pkg-c ${left > 0 ? "hv4-pkg-left" : "hv4-pkg-zero"}`}>
            {left}
          </b>
          <span role="cell" className="hv4-pkg-c">
            <button
              type="button"
              className={`hv4-pkg-hist-btn${logOpen ? " hv4-pkg-hist-btn--on" : ""}`}
              title={label}
              aria-label={label}
              onClick={() => props.onToggleLog(logOpen ? null : item.id)}
            >
              <History size={22} />
            </button>
          </span>
        </div>
        {logOpen && (
          <div className="hv4-pkg-log">
            <b>ประวัติการใช้งาน · {item.product_name}</b>
            {logs.length === 0 ? (
              <span className="hv4-pkg-log-none">ยังไม่มีการใช้งาน</span>
            ) : (
              logs.map((log, index) => (
                <div key={index} className="hv4-pkg-log-line">
                  <span className="hv4-pkg-log-at">{log.at}</span>
                  <span>{log.text}</span>
                </div>
              ))
            )}
          </div>
        )}
      </Fragment>
    );
  };

  const renderItems = (order: PackageOrder) => (
    <div className="hv4-pkg-hbody">
      <div className="hv4-pkg-paidline">{paidLineText(order)}</div>
      <div className="hv4-pkg-scroll-x">
        <div className="hv4-pkg-items" role="table" aria-label={order.product_name}>
          <div className="hv4-pkg-irow hv4-pkg-ihead" role="row">
            <span role="columnheader">รายการ</span>
            <span role="columnheader">สิทธิ์</span>
            <span role="columnheader" className="hv4-pkg-c">
              ใช้แล้ว
            </span>
            <span role="columnheader" className="hv4-pkg-c">
              คงเหลือ
            </span>
            <span role="columnheader" className="hv4-pkg-c">
              History
            </span>
          </div>
          {orderItemGroups(order).map((group) => (
            <Fragment key={group.category}>
              <div className="hv4-pkg-icat">
                <b>{group.category}</b> <span>({group.items.length} รายการ)</span>
              </div>
              {group.items.map(renderItem)}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCard = (order: PackageOrder) => {
    const open = props.openCards.includes(order.id);
    const done = isUsedUp(order);

    return (
      <div key={order.id} className="hv4-card hv4-pkg-hcard">
        <button
          type="button"
          className="hv4-pkg-hcard-head"
          aria-expanded={open}
          aria-label={`${open ? "ย่อ" : "ขยาย"} ${order.product_name}`}
          onClick={() => props.onToggleCard(order.id)}
        >
          <span className="hv4-pkg-chev">
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
          <b className="hv4-pkg-hname">{order.product_name}</b>
          <span className={`hv4-pkg-badge${done ? " hv4-pkg-badge--done" : ""}`}>
            {done ? "ใช้ครบแล้ว" : "ใช้งานอยู่"}
          </span>
          <span className="hv4-pkg-hsum">{historySummary(order)}</span>
        </button>
        {open && renderItems(order)}
      </div>
    );
  };

  return (
    <section className="hv4-pkg-hist">
      {renderChips()}
      <div className="hv4-pkg-hist-list">
        {rows.length === 0 && props.loading ? (
          <div className="hv4-card hv4-pkg-hist-empty">กำลังโหลด</div>
        ) : rows.length === 0 ? (
          <div className="hv4-card hv4-pkg-hist-empty">
            ยังไม่มีแพ็กเกจที่ชำระแล้ว · เลือกแพ็กเกจและชำระเงินก่อน
            จึงจะเริ่มใช้สิทธิ์และเห็นยอดคงเหลือได้
          </div>
        ) : (
          rows.map(renderCard)
        )}
      </div>
    </section>
  );
};

PackageHistoryTabV4.displayName = "PackageHistoryTabV4";

export default PackageHistoryTabV4;
